/**
 * Pages that must not advertise a Markdown version. Mirrors `robots.disallow`
 * in nuxt.config.ts. The wizard renders in the browser, so its Markdown has no
 * content (`/apply.md` is only the site front matter, and Cloudflare's `.md`
 * rewrite (PS-1081) answers 404 below `/apply/`). `/edit-symbol` is the
 * Builder.io editor's page, and its `.md` URL answers 404.
 */
export const MARKDOWN_EXCLUDED_PREFIXES = ['/apply', '/edit-symbol']

const isUnder = (path: string, prefix: string) =>
  path === prefix || path.startsWith(`${prefix}/`)

const isPagePath = (path: string) => {
  const lowerPath = path.toLowerCase()

  return (
    path.startsWith('/') &&
    // Nuxt internals (`/_nuxt`, `/__nuxt_error`).
    !lowerPath.startsWith('/_') &&
    !isUnder(lowerPath, '/api') &&
    // Files such as `/llms.txt` or `/sitemap.xml`.
    !/\.[^/]*$/.test(path) &&
    !MARKDOWN_EXCLUDED_PREFIXES.some((prefix) => isUnder(lowerPath, prefix))
  )
}

/**
 * The `.md` URL Cloudflare serves for a page (AP-10887), or `null` when the
 * page has none. `/` maps to `/index.md`; any other path gets `.md` appended,
 * without its query, hash or trailing slash.
 */
export const getMarkdownAlternatePath = (path: string): string | null => {
  const pathname = path.split(/[?#]/)[0]!.replace(/\/+$/, '') || '/'

  if (!isPagePath(pathname)) {
    return null
  }

  return pathname === '/' ? '/index.md' : `${pathname}.md`
}

/** `Link` header value advertising a page's Markdown version. */
export const getMarkdownLinkHeader = (markdownPath: string) =>
  `<${markdownPath}>; rel="alternate"; type="text/markdown"`

/** The parts of Nitro's rendered response that the `Link` header depends on. */
export interface IRenderedResponse {
  statusCode?: number
  headers?: Record<string, string>
}

/**
 * Adds the `Link` header to a rendered page, keeping any existing one. Only a
 * 200 HTML page with a Markdown version gets it, so error pages never
 * advertise a `.md` URL that would 404.
 */
export const addMarkdownLinkHeader = (
  response: IRenderedResponse,
  path: string
): void => {
  const { headers, statusCode = 200 } = response
  const isHtml = headers?.['content-type']?.includes('text/html')
  const markdownPath = getMarkdownAlternatePath(path)

  if (!headers || statusCode !== 200 || !isHtml || !markdownPath) {
    return
  }

  const link = getMarkdownLinkHeader(markdownPath)

  headers.link = headers.link ? `${headers.link}, ${link}` : link
}
